import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Redirect, useHistory, useLocation } from "react-router-dom";
import Cookies from "js-cookie";
import { useTranslation } from "react-i18next";
import { FaLock } from "react-icons/fa";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Modal } from "reactstrap";
import { toast } from "react-toastify";
import PageHeader from "../../components/PageHeader";
import PageHeaderExportButton from "../../ui/components/PageHeaderExportButton";
import Loading from "../../components/utils/Loading";
import {
  ActivityDetailFillingTab,
  ActivityDetailFollowUpTab,
  ActivityDetailSortingTab,
  ActivityDetailSummaryTab,
} from "./ActivityDetailTabs";
import ActivityDetailDisplayRulesTab from "./components/ActivityDetailDisplayRulesTab";
import { useActivityCollectionDetailData } from "./hooks/useActivityCollectionDetailData";
import { useCollectionRowData } from "./hooks/useCollectionRowData";
import { useCollectionRowClient } from "./hooks/useCollectionRowClient";
import { useWasteCategoryAndClients } from "./hooks/useWasteCategoryAndClients";
import { exportActivityRowToCSV } from "./hooks/useCollectionExport";
import {
  ActivityCollectionCompliancePdfExportUi,
  useActivityCollectionCompliancePdfExport,
} from "./hooks/useActivityCollectionCompliancePdfExport";
import {
  ActivityCharacterizeIcon,
  ActivityCompleteInfoIcon,
  ActivityContainerSettingsIcon,
  ActivityTagIcon,
  ActivityUploadFileIcon,
  buildActivityShareExportItems,
} from "./components/ActivityShareExportItems";
import ActivityDetailActionsMenu from "./components/ActivityDetailActionsMenu";
import { NotesIcon } from "../../ui/components/MonitoringMetricIcons";
import {
  formatActivityDateTime,
  readActivityDetailNavigationState,
  resolveStatusPresentation,
} from "./utils/collectionRowUtils";
import { buildActivityDetailRecommendations } from "./utils/activityDetailAnalysis";
import { hasFillingTimeline } from "./utils/activityDetailDisplayRules";
import {
  canCharacterizeFromHistorique,
  getCameraId,
  getCharacterizeFromHistoriqueBlockReasonKey,
  parseCameraImageUrl,
} from "../../shared/utils/utils";
import { useIsAdminViewMode } from "../../hooks/useViewMode";
import ContainerModalComment from "../../components/ClientModalComment";
import ContainerModalClient from "../../components/ClientModalInputs";
import FileModal from "../../components/FileModal";
import {
  createCharacReview,
  getAzureBlob,
  saveContainerWeight,
  updateContentCategories,
} from "../../services/container";
import {
  buildCommentAuthorFromStorage,
  parseCommentsList,
} from "../../shared/utils/commentAuthor";
import {
  applyWasteCategoryToCycleSurfaces,
  resolveHistoryEntriesForFluxChange,
} from "./utils/applyWasteCategoryToCycleSurfaces";
import {
  buildMetricsDateRangeFromHistoryCycle,
  enqueueWasteStatisticsMetrics,
} from "../../services/wasteStatisticsMetrics";
import moment from "moment";

const volumeBadgeClassName =
  "inline-flex items-center justify-center rounded-full text-sm font-bold text-[#2563EB]";
const volumeBadgeStyle = {
  padding: "4px 12px",
  height: "30px",
  background: "#E6ECFD",
  borderRadius: "1000px",
};
const addressBadgeClassName =
  "inline-flex items-center px-3 py-1 rounded-full text-sm font-bold bg-white text-[#222222]";
const addressBadgeStyle = { border: "1.5px solid #030303" };

const ActivityCollectionDetailContent = ({
  timeLine,
  superContainerTimeLine,
  activeWasteCategory = "",
  superContainers,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("summary");
  const isAdminViewMode = useIsAdminViewMode();
  const [displayComment, setDisplayComment] = useState(false);
  const [comment, setComment] = useState({
    time: new Date().toISOString(),
    author: buildCommentAuthorFromStorage(),
    text: "",
  });
  const [commentLoading, setCommentLoading] = useState(false);
  const [reviewCommentsState, setReviewCommentsState] = useState([]);
  const [displayClient, setDisplayClient] = useState(false);
  const [displayTags, setDisplayTags] = useState(false);
  const [displayStatus, setDisplayStatus] = useState(false);
  const [newClient, setNewClient] = useState(false);
  const [totalWeight, setTotalWeight] = useState("");
  const [openFileModal, setOpenFileModal] = useState(false);
  const [outputClient, setOutputClient] = useState({});
  const [openModalToCharacterization, setOpenModalToCharacterization] =
    useState(false);
  const weightRef = useRef(null);
  const azureUrl = process.env.REACT_APP_AZURE_URL;

  const data = timeLine;
  const superContainerDataRow = superContainerTimeLine || false;
  const collectionKey = data ? `${data._measurement}_${data._time}` : null;
  const collectionData = useSelector((state) => state.collection?.data);
  const collectionInStore = collectionKey
    ? collectionData?.[collectionKey]
    : null;

  // Fetch when Redux has no data (e.g. refresh on /activity/detail).
  // Skip when navigating from the list where the row already loaded.
  const { refetch: refetchRowData } = useCollectionRowData(
    data,
    superContainerDataRow,
    {
      enabled: Boolean(data && !collectionInStore),
    },
  );

  const {
    API,
    isLoading,
    csvData,
    csvDataReady,
    dataSortedByAnomalies,
    historyId,
    setHistoryId,
    lastPackmatEfficiency,
    numberOfStars,
    numberOfPackmat,
    totalVolumeSortingError,
    totalWasteVolume,
    colorObject,
    labelObjectSeparate,
    valueObjectSeparate,
    dataNumberOfObject,
    numberOfObjects,
    dataAnomalyDelivery,
    labelsAnomalyDelivery,
    colorAnomalyDelivery,
    valueAnomalyDelivery,
    anomalyChartTotals,
    historyCycle,
    historyCyclePerFlow,
    numberOfAnomaly,
    findHistoryId,
  } = useActivityCollectionDetailData({
    data,
    superContainerDataRow,
  });

  const { corporationClients, wasteCategory } = useWasteCategoryAndClients();
  const dropOff =
    historyCycle?.length > 0
      ? historyCycle[historyCycle.length - 1]?._time
      : "";
  const imageData =
    historyCycle?.length > 0
      ? historyCycle[data?.showDepotCycle ? historyCycle.length - 1 : 0]
      : null;
  const {
    files,
    setFiles,
    clientName,
    setClientName,
    clientCode,
    setClientCode,
    clientAddress,
    setClientAddress,
    tags,
    setTags,
    clientRegisteredWeight,
    setClientRegisteredWeight,
    comments,
    setComments,
    wasteName,
    wasteCategoryName,
    setWasteCategoryName,
    checkStatus,
    rawCheckStatus,
    toCharacterization,
    clientId,
    setClientId,
    fetchContainerClientRegisteredInfo,
  } = useCollectionRowClient(
    data,
    dropOff,
    superContainers,
    corporationClients,
    historyCycle,
    imageData,
    process.env.REACT_APP_AZURE_URL,
  );
  const collectionFillingLevel = historyCycle?.[0]?.filling_level ?? 0;

  const { startPdfExport, uiProps: pdfExportUiProps } =
    useActivityCollectionCompliancePdfExport({
      timeLine: data,
      superContainerTimeLine: superContainerDataRow,
      historyCycle,
      files,
      clientRegisteredWeight,
      numberOfPackmat,
      comments,
      wasteName: wasteName || data?.wasteName || "",
      clientName,
      dropOff,
      collectionFillingLevel,
    });
  const statusPresentation = useMemo(
    () => resolveStatusPresentation(checkStatus, t, rawCheckStatus),
    [checkStatus, rawCheckStatus, t],
  );
  const recommendations = useMemo(
    () =>
      buildActivityDetailRecommendations({
        historyCycle,
        totalVolumeSortingError,
        totalWasteVolume,
        statusKey: statusPresentation.key,
        packmatActive: data?.packmatActive,
        numberOfPackmat,
      }),
    [
      data?.packmatActive,
      historyCycle,
      numberOfPackmat,
      statusPresentation.key,
      totalVolumeSortingError,
      totalWasteVolume,
    ],
  );
  const reviewComments = useMemo(() => {
    if (Array.isArray(collectionInStore)) {
      return collectionInStore.flatMap(
        (collection) => collection?.reviewComments || [],
      );
    }
    return collectionInStore?.reviewComments || [];
  }, [collectionInStore]);

  useEffect(() => {
    setReviewCommentsState(reviewComments);
  }, [reviewComments]);

  const saveCommentWeight = async (
    commentOverride = null,
    { changeWasteCategory = false, closeClient = false } = {},
  ) => {
    setCommentLoading(true);
    const activeComment = commentOverride || comment;
    let commentsList = parseCommentsList(comments);
    if (activeComment?.toDelete || activeComment?.text !== "") {
      const filtered = commentsList.filter(
        (item) => item.time !== activeComment.time,
      );
      commentsList = activeComment?.toDelete
        ? filtered
        : [...filtered, activeComment];
      setComment({
        time: new Date().toISOString(),
        author: buildCommentAuthorFromStorage(),
        text: "",
      });
    }
    setComments(commentsList);

    let id = data._id;
    if (superContainers?.containerObjectIdList?.includes(id)) {
      id = superContainers._id;
    }
    const cameraId = imageData ? getCameraId(imageData.image_url) : "";
    const blobName = `camera-ak_${cameraId}/output-client/${data._time.replace(/:/g, "_").slice(0, -1)}-camera-ak_${cameraId}-${id}-client.json`;
    const blobUrl = azureUrl + blobName;
    const payload = {
      id,
      weight:
        parseFloat(String(clientRegisteredWeight).replace(",", ".")) * 1000 ||
        0,
      time: data._time,
      modified_time: moment().utc().format("YYYY-MM-DDTHH:mm:ss[Z]"),
      client: JSON.stringify({
        name: clientName,
        code: clientCode,
        address: clientAddress,
        checkStatus: checkStatus || "-",
        tags: tags || [],
        ...(files?.length > 0 && { files }),
      }),
      waste_category: wasteCategoryName,
      blobName,
      comments: JSON.stringify(commentsList),
      toCharacterization,
      mongoClientId: clientId,
    };

    try {
      try {
        const azureResponse = await getAzureBlob({ blobUrl });
        await saveContainerWeight({ ...azureResponse.data, ...payload });
      } catch (error) {
        if (error.response?.status === 404) {
          await saveContainerWeight(payload);
        } else {
          throw error;
        }
      }
      if (
        changeWasteCategory &&
        wasteCategoryName &&
        wasteCategoryName !== "{}"
      ) {
        await updateContentCategories(
          { contentCategory: JSON.parse(wasteCategoryName).id },
          data._id,
        );

        try {
          const historyEntries = resolveHistoryEntriesForFluxChange({
            superContainerTimeLine: superContainerDataRow,
            timeLine: data,
            collectionData,
            historyCycleFallback: historyCycle,
          });

          await applyWasteCategoryToCycleSurfaces({
            wasteCategoryJson: wasteCategoryName,
            characterization: toCharacterization,
            containerId: data._id,
            historyEntries,
            azureUrl,
          });

          if (imageData?.image_url) {
            const { cameraId: metricsCameraId, imageName } =
              parseCameraImageUrl(imageData.image_url);
            const dateRange = buildMetricsDateRangeFromHistoryCycle(
              historyCycle,
              data._time,
            );
            if (metricsCameraId && dateRange) {
              await enqueueWasteStatisticsMetrics({
                cameraName: metricsCameraId,
                start: dateRange.start,
                stop: dateRange.stop,
                imageName,
              });
            }
          }
        } catch (fluxError) {
          console.error("applyWasteCategoryToCycleSurfaces failed:", fluxError);
        }
      }
      if (closeClient) {
        setDisplayClient(false);
        setDisplayTags(false);
        setNewClient(false);
        setTotalWeight(clientRegisteredWeight);
        await fetchContainerClientRegisteredInfo(id);
      } else {
        setDisplayComment(false);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setCommentLoading(false);
    }
  };

  const handleSaveWeight = async (_containerId, changeWasteCategory) => {
    await saveCommentWeight(null, {
      changeWasteCategory,
      closeClient: true,
    });
  };

  const handleSelectionChange = useCallback(
    (event) => {
      const value = event?.target?.value;
      if (!value) {
        setClientName("");
        setClientAddress("");
        setClientId("");
        return;
      }
      const selectedValue = JSON.parse(value);
      setClientName(selectedValue?.name);
      setClientAddress(selectedValue?.address);
      setClientId(selectedValue?._id);
    },
    [setClientName, setClientAddress, setClientId],
  );

  const canEditCollection = Boolean(
    historyCycle?.length && !data?.fictiveContainer?.active,
  );

  const handleOpenCollectionInfo = () => {
    if (!canEditCollection) return;
    setDisplayClient(true);
    setNewClient(false);
    setDisplayStatus(false);
    setDisplayTags(false);
  };

  const handleOpenTags = () => {
    if (!canEditCollection) return;
    setDisplayClient(true);
    setNewClient(false);
    setDisplayStatus(false);
    setDisplayTags(true);
  };

  const handleOpenContainerSettings = () => {
    const id = data?._id ?? data?._measurement ?? data?.id;
    if (!id) return;
    history.push(`/settings/containers/${id}`, {
      container: data,
      from: "activity",
      returnPath: location.pathname + location.search,
    });
  };

  const canCharacterize = canCharacterizeFromHistorique(imageData?.image_url);
  const characterizeBlockedReason = getCharacterizeFromHistoriqueBlockReasonKey(
    imageData?.image_url,
  );

  const handleSubmitSendToReview = async () => {
    if (!imageData?.image_url) return;
    const blockReason = getCharacterizeFromHistoriqueBlockReasonKey(
      imageData.image_url,
    );
    if (blockReason) {
      toast.error(t(blockReason), {
        className: "custom-toast",
      });
      return;
    }
    try {
      const cameraId = getCameraId(imageData.image_url);
      let collections = [];
      if (superContainerDataRow) {
        const leftSide = superContainerDataRow.find((item) =>
          item._value_image?.includes("-L.jpeg"),
        );
        const collection =
          collectionData?.[`${leftSide?._measurement}_${leftSide?._time}`];
        collections = collection ? [...collection] : [];
      } else {
        const collection = collectionData?.[collectionKey];
        collections = collection ? [collection] : [];
      }
      await Promise.all(
        collections.flatMap((collection) =>
          (collection?.historyCurrent || []).map((historyEntry) =>
            createCharacReview({
              container_id: historyEntry._measurement,
              camera_id: cameraId,
              time: historyEntry._time,
              imagesrc: historyEntry.image_url,
            }),
          ),
        ),
      );
      toast.success(t("Review request sent"), {
        className: "custom-toast",
      });
      await fetchContainerClientRegisteredInfo(data._id);
    } catch (error) {
      console.error("createCharacReview failed:", error);
      toast.error(t("Unable to send review request"), {
        className: "custom-toast",
      });
    }
  };

  const handleAddComment = () => {
    setComment({
      time: new Date().toISOString(),
      author: buildCommentAuthorFromStorage(),
      text: "",
    });
    setDisplayComment(true);
  };

  const handleEditComment = (existingComment) => {
    setComment({ ...existingComment, toDelete: false });
    setDisplayComment(true);
  };

  const handleDeleteComment = (existingComment) => {
    saveCommentWeight({ ...existingComment, toDelete: true });
  };

  const flowTitle =
    activeWasteCategory !== "" ? activeWasteCategory : data?.wasteName || "-";

  const volumeLabel =
    data?.volume > 0 ? `${data.volume}${data.volumeUnit || ""}` : "-";

  const subtitle = useMemo(() => {
    if (!data) return "";

    const positioning = data?.positioning
      ? data.positioning.charAt(0).toUpperCase() + data.positioning.slice(1)
      : "";

    const formatDateTime = (time) =>
      time ? formatActivityDateTime(time, t) : "-";

    // Same semantics as the hub (Drop-off / Pick-up): the drop-off date is the
    // oldest entry of the cycle, the pickup/collection date is the row itself.
    const hasSeparateWithdrawal =
      historyCycle?.length > 1 &&
      (data.showDepotCycle === undefined || !data.showDepotCycle);
    const depositTime =
      historyCycle?.length > 0
        ? historyCycle[historyCycle.length - 1]._time
        : data._time;

    const depositLabel = `${t("Drop-off")} : ${formatDateTime(depositTime)}`;
    const withdrawalLabel = hasSeparateWithdrawal
      ? ` - ${t("Pick-up")} : ${formatDateTime(data._time)}`
      : "";

    return `${positioning} - ${depositLabel}${withdrawalLabel}`;
  }, [data, historyCycle, t]);

  const exportItems = useMemo(
    () =>
      buildActivityShareExportItems({
        t,
        onRequestPdf: () => startPdfExport("export"),
        onRequestCsv: () =>
          exportActivityRowToCSV(csvData, data, activeWasteCategory, t),
        onRequestEmail: () => startPdfExport("share"),
      }),
    [csvData, data, activeWasteCategory, t, startPdfExport],
  );

  const actionMenuItems = [
    {
      key: "info",
      label: t("Weight and producer"),
      icon: <ActivityCompleteInfoIcon />,
      onClick: handleOpenCollectionInfo,
    },
    {
      key: "files",
      label: t("Upload file"),
      icon: <ActivityUploadFileIcon />,
      onClick: () => {
        if (!canEditCollection || !imageData?.image_url) return;
        setOpenFileModal(true);
      },
    },
    {
      key: "comment",
      label: t("Add a comment"),
      icon: <NotesIcon />,
      onClick: handleAddComment,
    },
    {
      key: "tags",
      label: t("Add a tag"),
      icon: <ActivityTagIcon />,
      onClick: handleOpenTags,
    },
    {
      key: "characterize",
      label: t("To characterize"),
      icon: <ActivityCharacterizeIcon />,
      disabled: toCharacterization || !canCharacterize,
      title:
        !canCharacterize && characterizeBlockedReason
          ? t(characterizeBlockedReason)
          : undefined,
      onClick: () => {
        if (toCharacterization || !canCharacterize) return;
        setOpenModalToCharacterization(true);
      },
    },
    {
      key: "settings",
      label: `${t("Settings")} ${t("Container")}`,
      icon: <ActivityContainerSettingsIcon />,
      onClick: handleOpenContainerSettings,
    },
  ];

  const followUpTabLabel = `${t("Follow-up and documents")} (${files?.length || 0})`;
  const showFillingTimeline = hasFillingTimeline(historyCycle);
  const isSuperContainer =
    Array.isArray(superContainerDataRow) && superContainerDataRow.length > 0;

  useEffect(() => {
    if ((!showFillingTimeline || !isAdminViewMode) && activeTab === "filling") {
      setActiveTab("summary");
    }
    if (!isAdminViewMode && activeTab === "displayRules") {
      setActiveTab("summary");
    }
  }, [activeTab, isAdminViewMode, showFillingTimeline]);

  const detailTabs = [
    { id: "summary", label: t("Summary") },
    ...(showFillingTimeline && isAdminViewMode
      ? [
          {
            id: "filling",
            label: t("Filling"),
            icon: <FaLock className="text-[11px] opacity-70" aria-hidden />,
          },
        ]
      : []),
    { id: "sorting", label: t("Sorting and materials") },
    { id: "followUp", label: followUpTabLabel },
    ...(isAdminViewMode
      ? [{ id: "displayRules", label: t("activityDetailDisplayRules.tab") }]
      : []),
  ];

  const distributionChartProps = useMemo(
    () => ({
      activatedSortingErrorOption: data?.activatedSortingErrorOption,
      dataAnomalyDelivery,
      labelsAnomalyDelivery,
      colorAnomalyDelivery,
      valueAnomalyDelivery,
      numberOfAnomaly,
      anomalyChartTotals,
      dataNumberOfObject,
      labelObjectSeparate,
      valueObjectSeparate,
      numberOfObjects,
      colorObject,
      container: data,
      historyCycle,
      historyCyclePerFlow,
      findHistoryId,
      showWeightChart:
        (!superContainerDataRow || superContainerDataRow.length === 0) &&
        !data?.showDepotCycle &&
        historyCycle?.length > 1,
    }),
    [
      data,
      dataAnomalyDelivery,
      labelsAnomalyDelivery,
      colorAnomalyDelivery,
      valueAnomalyDelivery,
      numberOfAnomaly,
      anomalyChartTotals,
      dataNumberOfObject,
      labelObjectSeparate,
      valueObjectSeparate,
      numberOfObjects,
      colorObject,
      historyCycle,
      historyCyclePerFlow,
      findHistoryId,
      superContainerDataRow,
    ],
  );

  const titleBadges = (
    <>
      <span className={volumeBadgeClassName} style={volumeBadgeStyle}>
        {volumeLabel}
      </span>
      <span className={addressBadgeClassName} style={addressBadgeStyle}>
        {data?.addressName || "-"}
      </span>
    </>
  );
  const activityProps = {
    dataSortedByAnomalies,
    historyId,
    lastPackmatEfficiency,
    numberOfStars,
    numberOfPackmat,
    totalVolumeSortingError,
    totalWasteVolume,
    colorObject,
    labelObjectSeparate,
    dataNumberOfObject,
    numberOfObjects,
    dataAnomalyDelivery,
    labelsAnomalyDelivery,
    colorAnomalyDelivery,
    anomalyChartTotals,
    historyCycle,
    data,
    numberOfAnomaly,
    superContainerDataRow,
    superContainer: Array.isArray(superContainers)
      ? superContainers.find((item) =>
          item?.containerObjectIdList?.some((id) =>
            superContainerDataRow?.some?.((row) => row._id === id),
          ),
        ) || null
      : superContainers || null,
    findHistoryId,
    setHistoryId,
    API,
    onAfterSegmentationUpdate: refetchRowData,
  };
  const filePanelProps = {
    data,
    files,
    setFiles,
    superContainers,
    clientName,
    clientCode,
    clientAddress,
    tags,
    clientRegisteredWeight,
    comments,
    wasteCategoryName,
    checkStatus,
    cameraId: imageData ? getCameraId(imageData.image_url) : "",
    toCharacterization,
    clientId,
  };

  return (
    <div className="settings-page-wrapper" style={{ minWidth: "850px" }}>
      <PageHeader
        showBackButton
        backRoute="/activity"
        backLabel={t("Back to Activity")}
        titleContent={
          <div className="flex items-center gap-3 flex-wrap">
            <span>{flowTitle}</span>
            {titleBadges}
          </div>
        }
        titleContentMain={<span>{flowTitle}</span>}
        titleContentBadges={titleBadges}
        subtitle={subtitle}
        tabs={detailTabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        actions={
          <div className="flex items-center gap-3">
            <ActivityDetailActionsMenu items={actionMenuItems} />
            <PageHeaderExportButton
              disabled={isLoading || !csvDataReady}
              items={exportItems}
              label={t("Share")}
            />
          </div>
        }
      />

      <ActivityCollectionCompliancePdfExportUi {...pdfExportUiProps} />

      <div className="settings-content-wrapper">
        {activeTab === "summary" &&
          (isLoading ? (
            <Loading color="#efefeffb" />
          ) : (
            <ActivityDetailSummaryTab
              data={data}
              historyCycle={historyCycle}
              clientRegisteredWeight={clientRegisteredWeight}
              numberOfStars={numberOfStars}
              totalVolumeSortingError={totalVolumeSortingError}
              totalWasteVolume={totalWasteVolume}
              recommendations={recommendations}
              activityProps={activityProps}
            />
          ))}

        {activeTab === "filling" &&
          showFillingTimeline &&
          isAdminViewMode &&
          (isLoading ? (
            <Loading color="#efefeffb" />
          ) : (
            <ActivityDetailFillingTab
              data={data}
              historyCycle={historyCycle}
              findHistoryId={findHistoryId}
              numberOfPackmat={numberOfPackmat}
              onAfterUpdate={refetchRowData}
            />
          ))}

        {activeTab === "sorting" &&
          (isLoading ? (
            <Loading color="#efefeffb" />
          ) : (
            <ActivityDetailSortingTab
              statisticsProps={distributionChartProps}
            />
          ))}

        {activeTab === "followUp" && (
          <ActivityDetailFollowUpTab
            comments={comments}
            reviewComments={reviewCommentsState}
            filePanelProps={filePanelProps}
            onAddComment={handleAddComment}
            onEditComment={handleEditComment}
            onDeleteComment={handleDeleteComment}
          />
        )}

        {activeTab === "displayRules" && isAdminViewMode && (
          <ActivityDetailDisplayRulesTab
            historyCycle={historyCycle}
            data={data}
            dataSortedByAnomalies={dataSortedByAnomalies}
            isSuperContainer={isSuperContainer}
          />
        )}
      </div>

      <ContainerModalClient
        data={data}
        displayClient={displayClient}
        setDisplayClient={setDisplayClient}
        weightRef={weightRef}
        newClient={newClient}
        setNewClient={setNewClient}
        handleSelectionChange={handleSelectionChange}
        clientCode={clientCode}
        setClientCode={setClientCode}
        clientId={clientId}
        setClientName={setClientName}
        setClientAddress={setClientAddress}
        weight={clientRegisteredWeight}
        setWeight={setClientRegisteredWeight}
        wasteCategoryName={wasteCategoryName}
        setWasteCategoryName={setWasteCategoryName}
        wasteCategory={wasteCategory}
        saveWeight={handleSaveWeight}
        totalWeight={totalWeight}
        superContainer={superContainers}
        isCharacterization={false}
        refreshAction={null}
        loading={commentLoading}
        tags={tags}
        setTags={setTags}
        displayStatus={displayStatus}
        setDisplayStatus={setDisplayStatus}
        displayTags={displayTags}
        setDisplayTags={setDisplayTags}
        wasteName={wasteName}
      />

      <ContainerModalComment
        data={data}
        displayComment={displayComment}
        setDisplayComment={setDisplayComment}
        superContainer={false}
        saveWeight={saveCommentWeight}
        comments={comments}
        comment={comment}
        setComment={setComment}
        reviewComments={reviewCommentsState}
        setReviewComments={setReviewCommentsState}
        time={data._time}
        loading={commentLoading}
        setLoading={setCommentLoading}
      />

      {imageData?.image_url && (
        <FileModal
          data={data}
          openFileModal={openFileModal}
          setOpenFileModal={setOpenFileModal}
          outputClient={outputClient}
          setOutputClient={setOutputClient}
          files={files}
          setFiles={setFiles}
          superContainers={superContainers}
          clientName={clientName}
          clientCode={clientCode}
          clientAddress={clientAddress}
          tags={tags}
          clientRegisteredWeight={clientRegisteredWeight}
          comments={comments}
          wasteCategoryName={wasteCategoryName}
          checkStatus={checkStatus}
          cameraId={getCameraId(imageData.image_url)}
          toCharacterization={toCharacterization}
          clientId={clientId}
        />
      )}

      <Modal
        isOpen={openModalToCharacterization}
        toggle={() =>
          setOpenModalToCharacterization(!openModalToCharacterization)
        }
        className="w-2/6"
      >
        <section className="flex justify-between py-2 px-2 border-b-1">
          <h5 className="text-blue">{t("To characterize")}</h5>
          <FontAwesomeIcon
            icon="times"
            className="close-modal"
            onClick={() =>
              setOpenModalToCharacterization(!openModalToCharacterization)
            }
          />
        </section>
        <section className="bg-white rounded-md p-3 w-auto">
          {t("toCharacterizationMessage")}
        </section>
        <div className="w-full flex flex-end gap-2 pr-3 py-2 border-t-1">
          <button
            type="button"
            className="p-2 mr-2 border border-gray-300 bg-neutral-100 rounded-lg hover:bg-neutral-300"
            onClick={() =>
              setOpenModalToCharacterization(!openModalToCharacterization)
            }
          >
            {t("Cancel")}
          </button>
          <button
            type="button"
            className="p-2 w-28 text-md font-medium text-white rounded-lg bg-[#00A7A6]"
            onClick={async () => {
              await handleSubmitSendToReview();
              setOpenModalToCharacterization(false);
            }}
          >
            {t("Validate")}
          </button>
        </div>
      </Modal>
    </div>
  );
};

const ActivityCollectionDetailView = () => {
  const token = Cookies.get("userToken");
  const location = useLocation();
  const detailState = useMemo(
    () => readActivityDetailNavigationState(location),
    [location],
  );

  if (!token) {
    return <Redirect to={{ pathname: "/login" }} />;
  }

  if (!detailState?.timeLine) {
    return <Redirect to={{ pathname: "/activity" }} />;
  }

  return <ActivityCollectionDetailContent {...detailState} />;
};

export default ActivityCollectionDetailView;
